import { Component, computed, effect, inject, signal } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD, KENDO_GRIDLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgreementCreateDto, AgreementsClient, CreatedResponseDto, FormTemplateClient, UnityFormType } from '@unity/clients';
import { DropdownAgencyControlComponent } from '@unity/forms/dropdown-agency-control/dropdown-agency-control.component';
import { DropdownFormSchemasControlComponent } from '@unity/forms/dropdown-form-schemas-control/dropdown-form-schemas-control.component';
import { DropdownProgramControlComponent } from '@unity/forms/dropdown-program-control/dropdown-program-control.component';
import { MultiselectCommunityControlComponent } from '@unity/forms/multiselect-community-control/multiselect-community-control.component';
import { RadioConfirmationControlComponent } from '@unity/forms/radio-confirmation-control/radio-confirmation-control.component';
import { SelectorDateControlComponent } from '@unity/forms/selector-date-control/selector-date-control.component';
import { SelectorDateRangeControlComponent } from '@unity/forms/selector-date-range-control/selector-date-range-control.component';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { TextboxCurrencyControlComponent } from '@unity/forms/textbox-currency-control/textbox-currency-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { LayoutService } from '@unity/services/layout.service';
import { ActionCancelSharedComponent } from '@unity/shared/composed/action-cancel-shared/action-cancel-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import {
  booleanFormControl,
  dateFormControl,
  nonZeroFormControl,
  optionalNumericFormControl,
  optionalStringFormControl,
  stringFormControl,
} from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { switchMap } from 'rxjs';

class AgreementCreateSchema implements FormGroupControlSchema<AgreementCreateDto> {
  public name = stringFormControl();
  public description = optionalStringFormControl();
  public award_amount = optionalNumericFormControl({ validators: [Validators.min(0.01), Validators.max(99999999.99)] });
  public client_share_of_award_amount = optionalNumericFormControl({ validators: [Validators.min(0.01), Validators.max(99999999.99)] });
  public client_standard_allocation_amount = optionalNumericFormControl({
    validators: [Validators.min(0.01), Validators.max(99999999.99)],
  });
  public agency_id = nonZeroFormControl();
  public program_id = nonZeroFormControl();
  public community_ids = new FormControl<number[]>([], { nonNullable: true });
  public agreement_date = dateFormControl();
  public start_date = dateFormControl();
  public end_date = dateFormControl();
  public supports_clients = booleanFormControl();
  public supports_client_expenses = booleanFormControl();
  public supports_disbursements = booleanFormControl();
  public supports_documents = booleanFormControl();
  public supports_forms = booleanFormControl();
  public supports_reference_files = booleanFormControl();
  public client_form_schema_id = optionalNumericFormControl();
  public client_expense_form_schema_id = optionalNumericFormControl();
  public disbursement_form_schema_id = optionalNumericFormControl();
}

@Component({
  selector: 'unity-new-agreement-page',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_LOADER,
    KENDO_GRIDLAYOUT,
    ReactiveFormsModule,
    TextboxControlComponent,
    DropdownAgencyControlComponent,
    TextboxCurrencyControlComponent,
    DropdownProgramControlComponent,
    SelectorDateControlComponent,
    MultiselectCommunityControlComponent,
    SelectorDateRangeControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    RadioConfirmationControlComponent,
    TextareaControlComponent,
    DropdownFormSchemasControlComponent,
    ActionSharedComponent,
    ActionCancelSharedComponent,
  ],
  templateUrl: './new-agreement-page.component.html',
  styleUrl: './new-agreement-page.component.scss',
})
export class NewAgreementPageComponent extends AbstractNewComponent<AgreementCreateDto, AgreementCreateSchema, CreatedResponseDto> {
  private readonly layoutService = inject(LayoutService);
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly formSchemasClient = inject(FormTemplateClient);

  protected override readonly $contentHeader = signal('Create New Agreement');
  protected override createResourceFn$ = (form: FormGroup<AgreementCreateSchema>) => this.agreementsClient.createOne(form.getRawValue());
  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => ['/agreement', response.id, 'view'];
  protected override newControlsFn() {
    return new AgreementCreateSchema();
  }

  public constructor() {
    super();

    effect(() => {
      const supportsClients = this.$supportsClients();
      const formGroup = this.$formGroup();

      // If "Supports Clients" is false, set "Supports Client Expenses" to false.
      if (formGroup && !supportsClients) {
        formGroup.controls.supports_client_expenses.setValue(false);
      }

      // Else if "Supports Clients" is true, reset "Supports Client Expenses" to a blank value.
      else if (formGroup && supportsClients) {
        formGroup.controls.supports_client_expenses.reset();
      }
    });
  }

  protected readonly $supportsClients = toSignal(
    toObservable(this.$formGroup).pipe(switchMap(x => x.controls.supports_clients.valueChanges))
  );

  protected readonly $supportsClientExpenses = toSignal(
    toObservable(this.$formGroup).pipe(switchMap(x => x.controls.supports_client_expenses.valueChanges))
  );

  protected readonly $supportsDisbursments = toSignal(
    toObservable(this.$formGroup).pipe(switchMap(x => x.controls.supports_disbursements.valueChanges))
  );

  private formSchemasResource = rxResource({
    stream: () => this.formSchemasClient.getAllAsMini(),
  });

  protected readonly $formSchemas = computed(() => this.formSchemasResource.value()?.data ?? []);

  protected readonly $clientSchemas = computed(() => {
    const allSchemas = this.$formSchemas();
    return allSchemas.filter(schema => schema.type_enum_value === UnityFormType.Client);
  });

  protected readonly $clientExpenseSchemas = computed(() => {
    const allSchemas = this.$formSchemas();
    return allSchemas.filter(schema => schema.type_enum_value === UnityFormType.ClientExpense);
  });

  protected readonly $disbursementSchemas = computed(() => {
    const allSchemas = this.$formSchemas();
    return allSchemas.filter(schema => schema.type_enum_value === UnityFormType.Disbursement);
  });

  protected returnToDashboardAction = QuickActions.ReturnToDashboard;

  protected readonly $cols = computed(() => {
    const orientation = this.layoutService.$orientation();
    return orientation === 'vertical' ? [{ width: '1fr' }] : [{ width: '1fr' }, { width: '1fr' }, { width: '1fr' }];
  });
}
